/**
 * Helpers for the per-state `agents` object (see the comment above
 * `WorkflowStateAgents` in types/workflow.ts for why this has no server-published
 * schema). Parsing is passthrough-first: unrecognised keys on the top-level object
 * and on each agent are preserved untouched, only the fields the editor renders are
 * normalized to a predictable type.
 */
import type {
  WorkflowCoordinatorAgent,
  WorkflowFrontAgent,
  WorkflowStateAgents,
  WorkflowValidatorAgent,
  WorkflowWorkerAgent,
} from '../types/workflow';

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asStr(v: unknown, fallback = ''): string {
  return typeof v === 'string' ? v : fallback;
}

function asOptStr(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined;
}

function asNum(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function asOptNum(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function asOptBool(v: unknown): boolean | undefined {
  return typeof v === 'boolean' ? v : undefined;
}

function asStrArr(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const out = v.filter((x): x is string => typeof x === 'string');
  return out.length ? out : undefined;
}

export function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

// ─── Parse (server object → typed, still-passthrough object) ──────────────

export function parseCoordinatorAgent(raw: unknown): WorkflowCoordinatorAgent {
  const src = isPlainObject(raw) ? raw : {};
  return {
    ...src,
    id: asOptStr(src.id),
    name: asStr(src.name, 'Coordinator'),
    model: asStr(src.model, 'gpt-4.1-mini'),
    prompt: asStr(src.prompt),
    temperature: asNum(src.temperature, 0.7),
    description: asOptStr(src.description),
    reminder_prompt: asOptStr(src.reminder_prompt),
    reminder_prompt_enabled: asOptBool(src.reminder_prompt_enabled),
    models_reasoning: asOptStr(src.models_reasoning),
    state_machine_tools: asStrArr(src.state_machine_tools),
    use_rag: asOptBool(src.use_rag),
    rag_doc_ids: asStrArr(src.rag_doc_ids),
    use_ltm: asOptBool(src.use_ltm),
    use_diary_read: asOptBool(src.use_diary_read),
    use_diary_write: asOptBool(src.use_diary_write),
    use_portrait_read: asOptBool(src.use_portrait_read),
    use_portrait_write: asOptBool(src.use_portrait_write),
    use_user_message_injection_tool: asOptBool(src.use_user_message_injection_tool),
  };
}

export function parseFrontAgent(raw: unknown): WorkflowFrontAgent {
  const src = isPlainObject(raw) ? raw : {};
  return {
    ...src,
    id: asOptStr(src.id),
    name: asStr(src.name, 'Front'),
    model: asStr(src.model, 'gpt-4.1-mini'),
    prompt: asStr(src.prompt),
    temperature: asNum(src.temperature, 0.7),
    description: asOptStr(src.description),
    reminder_prompt: asOptStr(src.reminder_prompt),
    models_reasoning: asOptStr(src.models_reasoning),
  };
}

export function parseWorkerAgent(raw: unknown): WorkflowWorkerAgent {
  const src = isPlainObject(raw) ? raw : {};
  const mode = src.mode === 'asynchronous' ? 'asynchronous' : 'synchronous';
  return {
    ...src,
    id: asStr(src.id) || uuid(),
    name: asStr(src.name, 'Worker'),
    model: asStr(src.model, 'gpt-4.1-mini'),
    prompt: asStr(src.prompt),
    temperature: asNum(src.temperature, 0.7),
    mode,
    enabled: src.enabled === false ? false : true,
    description: asOptStr(src.description),
    reminder_prompt: asOptStr(src.reminder_prompt),
    models_reasoning: asOptStr(src.models_reasoning),
    state_context_key: asOptStr(src.state_context_key),
    use_state_context: asOptBool(src.use_state_context),
    state_machine_tools: asStrArr(src.state_machine_tools),
    use_rag: asOptBool(src.use_rag),
    rag_doc_ids: asStrArr(src.rag_doc_ids),
    use_ltm: asOptBool(src.use_ltm),
    use_diary_read: asOptBool(src.use_diary_read),
    use_diary_write: asOptBool(src.use_diary_write),
    use_portrait_read: asOptBool(src.use_portrait_read),
    use_portrait_write: asOptBool(src.use_portrait_write),
    use_user_message_injection_tool: asOptBool(src.use_user_message_injection_tool),
  };
}

export function parseValidatorAgent(raw: unknown): WorkflowValidatorAgent {
  const src = isPlainObject(raw) ? raw : {};
  return {
    ...src,
    name: asStr(src.name, 'Validator'),
    model: asStr(src.model, 'gpt-4.1-mini'),
    rules: asStrArr(src.rules) || [],
    max_retries: asNum(src.max_retries, 1),
    models_reasoning: asOptStr(src.models_reasoning),
    enabled: asOptBool(src.enabled),
    max_length: asOptNum(src.max_length),
    alliance_check: asOptBool(src.alliance_check),
    alliance_check_prompt: asOptStr(src.alliance_check_prompt),
    on_exhausted: asOptStr(src.on_exhausted),
    fallback_reply: asOptStr(src.fallback_reply),
  };
}

/** `null` when `raw` is not an object at all — the caller decides how to render that (empty state vs. raw JSON fallback). */
export function parseStateAgents(raw: unknown): WorkflowStateAgents | null {
  if (!isPlainObject(raw)) return null;
  const metaSrc = raw.meta_prompts;
  const meta_prompts = isPlainObject(metaSrc)
    ? (Object.fromEntries(Object.entries(metaSrc).filter(([, v]) => typeof v === 'string')) as Record<string, string>)
    : undefined;
  return {
    ...raw,
    // Every real payload from the target system includes a coordinator whenever `agents` is
    // present at all — but never fabricate one for a payload that genuinely omits it.
    coordinator: raw.coordinator != null ? parseCoordinatorAgent(raw.coordinator) : undefined,
    front: raw.front != null ? parseFrontAgent(raw.front) : null,
    workers: Array.isArray(raw.workers) ? raw.workers.map(parseWorkerAgent) : [],
    validator: raw.validator != null ? parseValidatorAgent(raw.validator) : null,
    meta_prompts,
  };
}

// ─── Blank builders ─────────────────────────────────────────────────────────

export function blankStateAgents(): WorkflowStateAgents {
  return { coordinator: parseCoordinatorAgent(undefined) };
}

export function blankFrontAgent(): WorkflowFrontAgent {
  return parseFrontAgent(undefined);
}

export function blankWorkerAgent(overrides: Partial<WorkflowWorkerAgent> = {}): WorkflowWorkerAgent {
  return parseWorkerAgent(overrides);
}

/**
 * A worker scoped to a specific slot of persistent state context — the target system resolves it
 * by `state_context_key` (or by name, if no key is set) rather than treating it as an interchangeable
 * sync/async worker. Grouped separately in the editor so its purpose reads at a glance.
 */
export function isSpecialWorker(w: WorkflowWorkerAgent): boolean {
  return Boolean(w.use_state_context) || Boolean((w.state_context_key || '').trim());
}

export function blankValidatorAgent(): WorkflowValidatorAgent {
  return parseValidatorAgent(undefined);
}

// ─── Derived ────────────────────────────────────────────────────────────────

export function countStateAgents(raw: unknown): number {
  const a = parseStateAgents(raw);
  if (!a) return 0;
  let n = a.coordinator ? 1 : 0;
  if (a.front) n += 1;
  if (a.validator) n += 1;
  n += a.workers?.length || 0;
  return n;
}

export function stateAgentsSummary(raw: unknown): string {
  if (raw == null) return 'none';
  const a = parseStateAgents(raw);
  if (!a) return 'unrecognized value';
  const parts: string[] = ['coordinator'];
  if (a.front) parts.push('front');
  if (a.workers?.length) parts.push(`${a.workers.length} worker${a.workers.length === 1 ? '' : 's'}`);
  if (a.validator) parts.push('validator');
  return parts.join(' · ');
}

/** Every model id already in use on this state's agents — datalist suggestions for the model field. */
export function modelsInUse(agents: WorkflowStateAgents): string[] {
  const set = new Set<string>();
  if (agents.coordinator?.model) set.add(agents.coordinator.model);
  if (agents.front?.model) set.add(agents.front.model);
  if (agents.validator?.model) set.add(agents.validator.model);
  (agents.workers || []).forEach((w) => w.model && set.add(w.model));
  return Array.from(set).sort();
}
